import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./AliExpressCart.css";

export default function AliExpressCart({ isOpen, onClose }) {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadCart();
    }
  }, [isOpen, user]);

  const loadCart = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/aliexpress/cart`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCart(response.data);
    } catch (error) {
      console.error("Erro ao carregar carrinho:", error);
      // Se não existir carrinho, criar um vazio
      setCart({
        items: [],
        subtotal: 0,
        shipping: 0,
        discount: 0,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/aliexpress/cart/update`,
        {
          itemId,
          quantity: newQuantity,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await loadCart();
    } catch (error) {
      console.error("Erro ao atualizar quantidade:", error);
      alert("Erro ao atualizar quantidade");
    } finally {
      setUpdating(false);
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm("Deseja remover este item do carrinho?")) return;

    try {
      setUpdating(true);
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/aliexpress/cart/remove/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await loadCart();
    } catch (error) {
      console.error("Erro ao remover item:", error);
      alert("Erro ao remover item do carrinho");
    } finally {
      setUpdating(false);
    }
  };

  const handleCheckout = () => {
    if (!cart || cart.items.length === 0) {
      alert("Seu carrinho está vazio");
      return;
    }
    onClose();
    navigate("/aliexpress/checkout");
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="cart-overlay" onClick={onClose}></div>
      <div className={`cart-sidebar ${isOpen ? "open" : ""}`}>
        <div className="cart-header">
          <h2>Carrinho de Compras</h2>
          <button className="btn-close-cart" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <div className="cart-loading">
            <div className="spinner"></div>
            <p>Carregando carrinho...</p>
          </div>
        ) : cart && cart.items && cart.items.length > 0 ? (
          <>
            <div className="cart-items">
              {cart.items.map((item) => (
                <div key={item._id} className="cart-item">
                  <img src={item.product.image} alt={item.product.name} />
                  <div className="item-info">
                    <h4>{item.product.name}</h4>
                    {item.selectedVariant && (
                      <p className="item-variant">
                        {item.selectedVariant.color && (
                          <span>Cor: {item.selectedVariant.color}</span>
                        )}
                        {item.selectedVariant.size && (
                          <span>Tamanho: {item.selectedVariant.size}</span>
                        )}
                      </p>
                    )}
                    <div className="item-price">
                      {item.product.originalPrice &&
                      item.product.originalPrice > item.product.price ? (
                        <>
                          <span className="original-price">
                            {formatPrice(item.product.originalPrice)}
                          </span>
                          <span className="current-price">
                            {formatPrice(item.product.price)}
                          </span>
                        </>
                      ) : (
                        <span className="current-price">
                          {formatPrice(item.product.price)}
                        </span>
                      )}
                    </div>
                    <div className="item-actions">
                      <div className="quantity-controls">
                        <button
                          onClick={() =>
                            updateQuantity(item._id, item.quantity - 1)
                          }
                          disabled={updating || item.quantity <= 1}
                        >
                          −
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(item._id, item.quantity + 1)
                          }
                          disabled={
                            updating ||
                            (item.product.stock &&
                              item.quantity >= item.product.stock)
                          }
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="btn-remove"
                        onClick={() => removeItem(item._id)}
                        disabled={updating}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 6h18M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2m3 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6h14z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-summary">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formatPrice(cart.subtotal)}</span>
              </div>
              {cart.discount > 0 && (
                <div className="summary-row discount">
                  <span>Desconto</span>
                  <span>-{formatPrice(cart.discount)}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Frete</span>
                <span>
                  {cart.shipping === 0 ? "Grátis" : formatPrice(cart.shipping)}
                </span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatPrice(cart.total)}</span>
              </div>
            </div>

            <div className="cart-actions">
              <button className="btn-continue-shopping" onClick={onClose}>
                Continuar Comprando
              </button>
              <button
                className="btn-checkout"
                onClick={handleCheckout}
                disabled={updating}
              >
                Finalizar Compra
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          <div className="cart-empty">
            <svg
              width="80"
              height="80"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.3 9.4a1 1 0 001 1.6h11.6a1 1 0 001-1.6L17 13M9 21a1 1 0 100-2 1 1 0 000 2zM17 21a1 1 0 100-2 1 1 0 000 2z" />
            </svg>
            <h3>Seu carrinho está vazio</h3>
            <p>Adicione produtos para começar suas compras!</p>
            <button className="btn-start-shopping" onClick={onClose}>
              Ver Produtos
            </button>
          </div>
        )}

        {/* Cart Counter Badge - Floating */}
        {cart && cart.items && cart.items.length > 0 && (
          <div className="cart-counter-badge">{cart.items.length}</div>
        )}
      </div>
    </>
  );
}
